import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/en-gb';
import { tokens } from '../../../global/theme/tokens';
import { VariableTextField } from './VariableTextField';
import { OutOfOfficeMailboxPicker } from './OutOfOfficeMailboxPicker';
import { OutOfOfficePreview } from './OutOfOfficePreview';
import { StatusPill, getStatusMeta } from './OutOfOfficeCard';
import { ExoRichTextEditor } from '../../exo/ExoRichTextEditor';
import useFetch from '../../../api/useFetch';
import { useUserPreferences } from '../../../global/useUserPreferences';
import {
  OUT_OF_OFFICE_DATE_FORMAT,
  OUT_OF_OFFICE_VARS,
  buildVarValues,
  extractCredentialIds,
  getDurationInDays,
  getOutOfOfficeStatus,
  getVarPlaceholderLabel,
  isHtmlEmpty,
  plainTextToHtml,
  replaceVarChipsWithTokens,
  replaceVarTokensWithChips,
} from '../helpers/outOfOffice';
import { Delta } from 'quill';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const EMPTY_FORM = {
  startDate: null,
  endDate: null,
  subject: '',
  body: '',
  emailCredentialIds: [],
};

/**
 * Editor for a single out of office notification. Mount it with a `key` bound
 * to the edited entry so switching entries resets the local state.
 */
export const OutOfOfficeForm = ({
  startData,
  mailboxes = [],
  dateFormat = 'DD.MM.YYYY',
  isSaving,
  onSubmit,
  onCancel,
  onDelete,
  onBack,
  // Vacation mode: the period comes from the approved vacations, so there is
  // no date range to edit and the placeholders stay dynamic.
  hideDates,
  title,
  infoText,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const isEdit = Boolean(startData?.id);

  const [form, setForm] = useState(() =>
    startData
      ? {
          startDate: startData.startDate || null,
          endDate: startData.endDate || null,
          subject: startData.subject || '',
          body: startData.body || '',
          emailCredentialIds: extractCredentialIds(startData),
        }
      : { ...EMPTY_FORM },
  );
  const [showErrors, setShowErrors] = useState(false);

  // The body is HTML. The editor is seeded once from the entry (the form is
  // mounted with a `key` per entry) and afterwards only writes into the form
  // state — feeding the value back would reset the editor on every keystroke.
  // On the way in the raw `%%tokens%%` become atomic chips, on the way out the
  // chips become tokens again — like the subject field.
  const bodyEditorRef = useRef(null);
  const initialBody = useRef(
    replaceVarTokensWithChips(
      startData?.body || '',
      hideDates ? {} : buildVarValues(startData || {}, dateFormat),
    ),
  ).current;

  // Signatures are text blocks; which one a mailbox uses is stored in the user
  // preferences, mirroring the email composer (see NewEmail).
  const { apiData: signatureBlocks } = useFetch(`text-blocks?type[eq]=signature`);
  const { preferences } = useUserPreferences();

  // "en-gb" starts the week on Monday, matching the rest of the app.
  useEffect(() => {
    moment.locale('en-gb');
  }, []);

  // A new notification starts with a mailbox picked — a reply that answers from
  // nowhere does nothing, and most users only have the one. Runs as an effect
  // because the mailbox list can still be loading when the form mounts, and
  // only once, so clearing the selection by hand is not undone.
  const didPreselectMailbox = useRef(false);
  useEffect(() => {
    if (startData || didPreselectMailbox.current || !mailboxes.length) return;
    didPreselectMailbox.current = true;
    setForm((prev) =>
      prev.emailCredentialIds.length
        ? prev
        : { ...prev, emailCredentialIds: [mailboxes[0].id] },
    );
  }, [mailboxes, startData]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  function handleDateChange(key, newDate) {
    const value =
      newDate && newDate.isValid()
        ? newDate.format(OUT_OF_OFFICE_DATE_FORMAT)
        : null;

    // Keep the range sane: moving the start behind the end pushes the end along.
    if (key === 'startDate' && value && form.endDate && value > form.endDate)
      return update({ startDate: value, endDate: value });

    update({ [key]: value });
  }

  const errors = useMemo(() => {
    const next = {};
    if (!hideDates) {
      if (!form.startDate) next.startDate = t('Required');
      if (!form.endDate) next.endDate = t('Required');
      if (form.startDate && form.endDate && form.endDate < form.startDate)
        next.endDate = t('End date must be after the start date');
    }
    if (!form.subject.trim()) next.subject = t('Required');
    if (!form.body) next.body = t('Required');
    if (!form.emailCredentialIds.length)
      next.emailCredentialIds = t('Select at least one mailbox');
    return next;
  }, [form, t, hideDates]);

  const isValid = Object.keys(errors).length === 0;
  const visibleErrors = showErrors ? errors : {};

  // In vacation mode there is no concrete period to resolve against — the
  // placeholders stay as `[Start date]` / `[End date]` and the backend fills
  // them per vacation.
  const varValues = useMemo(
    () =>
      hideDates
        ? {}
        : buildVarValues(
            { startDate: form.startDate, endDate: form.endDate },
            dateFormat,
          ),
    [form.startDate, form.endDate, dateFormat, hideDates],
  );

  const duration = getDurationInDays(form.startDate, form.endDate);

  const selectedMailboxes = mailboxes.filter((mailbox) =>
    form.emailCredentialIds.includes(mailbox.id),
  );

  // Which signature belongs to the notification: the one mapped to the first
  // selected mailbox, falling back to the user's first signature block.
  const signature = (() => {
    const blocks = signatureBlocks?.data || [];
    if (!blocks.length) return null;
    const username = selectedMailboxes[0]?.imapUsername;
    const mappedId = username
      ? (preferences?.emailSignatureMap || {})[username]
      : null;
    return blocks.find((block) => block.id === mappedId) || blocks[0];
  })();
  const sender = selectedMailboxes.length
    ? `${selectedMailboxes[0].label} <${
        selectedMailboxes[0].smtpUsername || selectedMailboxes[0].imapUsername
      }>${
        selectedMailboxes.length > 1 ? ` +${selectedMailboxes.length - 1}` : ''
      }`
    : null;

  function handleSubmit() {
    if (!isValid) return setShowErrors(true);
    onSubmit({
      // The vacation reply has no period of its own — sending one would
      // override what the vacation records say.
      ...(hideDates
        ? {}
        : { startDate: form.startDate, endDate: form.endDate }),
      subject: form.subject.trim(),
      body: form.body,
      emailCredentialIds: form.emailCredentialIds,
    });
  }

  // Starting points that already contain the placeholders — offered only while
  // the notification is still empty.
  const templates = [
    {
      label: t('Vacation'),
      subject: t('Out of office until %%endDate%%'),
      body: t(
        'Hello,\n\nthank you for your message. I am out of office from %%startDate%% until %%endDate%% and will not be reading my emails during this time.\n\nYour message will not be forwarded automatically.\n\nBest regards',
      ),
    },
    {
      label: t('Business trip'),
      subject: t('Away on business from %%startDate%% to %%endDate%%'),
      body: t(
        'Hello,\n\nI am travelling on business from %%startDate%% to %%endDate%% and can only answer with a delay.\n\nIn urgent cases please contact my colleagues.\n\nBest regards',
      ),
    },
  ];
  const showTemplates = !form.subject && !form.body;

  // Loading a template fills the editor: the plain i18n template text turned
  // into HTML, with the mailbox signature appended like in the email composer.
  function applyTemplate(template) {
    const bodyHtml =
      plainTextToHtml(template.body) +
      (signature?.text ? '<p><br/></p>' + signature.text : '');

    update({ subject: template.subject, body: bodyHtml });
    // The editor mirrors the content back through onHtmlChange.
    bodyEditorRef.current?.setContent(
      replaceVarTokensWithChips(bodyHtml, varValues),
    );
  }

  // A changed date only relabels the chips in place — the caret and the stored
  // value (raw tokens) stay untouched. `form.body` is in the deps so freshly
  // inserted chips get their resolved label too.
  useEffect(() => {
    const root = bodyEditorRef.current?.getRoot?.();
    if (!root) return;

    root.querySelectorAll('.ooo-chip[data-var]').forEach((chip) => {
      const resolved = varValues[chip.dataset.var];
      const label = resolved || getVarPlaceholderLabel(chip.dataset.var);
      // Quill wraps an embed's content in an inner node between two guard text
      // nodes — only the content node may be rewritten.
      const content = chip.querySelector('[contenteditable="false"]') || chip;
      if (content.textContent !== label) content.textContent = label;
      chip.classList.toggle('ooo-chip-pending', !resolved && !hideDates);
    });
  }, [varValues, hideDates, form.body]);

  const statusMeta = getStatusMeta(getOutOfOfficeStatus(form), colors);

  return (
    <Box className="flex flex-col h-full w-full overflow-hidden">
      {/* header */}
      <Box className="flex items-center gap-2 pb-2">
        {onBack && (
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h5" fontWeight={600} className="flex-1" noWrap>
          {title || (isEdit ? t('Edit notification') : t('New notification'))}
        </Typography>
        {!hideDates && form.startDate && form.endDate && (
          <StatusPill
            status={getOutOfOfficeStatus(form)}
            statusMeta={statusMeta}
          />
        )}
        {isEdit && onDelete && !hideDates && (
          <Tooltip title={t('Delete notification')}>
            <IconButton
              size="small"
              onClick={() => onDelete(startData)}
              sx={{ color: colors.error }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* scrollable body */}
      <Box
        className="flex flex-col gap-3 flex-1 overflow-y-auto pr-1 pb-2"
        //The preview sets `overflow: hidden` for its
        // rounded corners, so its minimum is 0 and the layout would shrink it
        // and clip its content rather than let this column scroll.
        sx={{ '& > *': { flexShrink: 0 } }}
      >
        {infoText && (
          <Box
            className="flex items-start gap-2 rounded-lg p-2"
            sx={{
              bgcolor: colors.info + '18',
              border: `1px solid ${colors.info}50`,
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: '17px', color: colors.info, marginTop: '1px' }}
            />
            <Typography fontSize={12}>{infoText}</Typography>
          </Box>
        )}

        {!hideDates && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 rounded-lg p-2"
              sx={{ bgcolor: colors.card }}
            >
              <DatePicker
                className="flex-1"
                label={t('Start date')}
                format={dateFormat}
                value={
                  form.startDate
                    ? moment(form.startDate, OUT_OF_OFFICE_DATE_FORMAT)
                    : null
                }
                onChange={(date) => handleDateChange('startDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: Boolean(visibleErrors.startDate),
                  },
                }}
              />
              <Box className="flex flex-row sm:flex-col items-center justify-center gap-1 px-1">
                <ArrowRightAltIcon
                  className="opacity-50 rotate-90 sm:rotate-0"
                  sx={{ fontSize: '20px' }}
                />
                {duration && (
                  <Typography
                    fontSize={10}
                    fontWeight={700}
                    className="rounded-full px-2 whitespace-nowrap"
                    sx={{ bgcolor: colors.info + '30', color: colors.info }}
                  >
                    {`${duration} ${duration === 1 ? t('day') : t('days')}`}
                  </Typography>
                )}
              </Box>
              <DatePicker
                className="flex-1"
                label={t('End date')}
                format={dateFormat}
                minDate={
                  form.startDate
                    ? moment(form.startDate, OUT_OF_OFFICE_DATE_FORMAT)
                    : undefined
                }
                value={
                  form.endDate
                    ? moment(form.endDate, OUT_OF_OFFICE_DATE_FORMAT)
                    : null
                }
                onChange={(date) => handleDateChange('endDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: Boolean(visibleErrors.endDate),
                    helperText: visibleErrors.endDate,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        )}

        <OutOfOfficeMailboxPicker
          mailboxes={mailboxes}
          selectedIds={form.emailCredentialIds}
          onChange={(ids) => update({ emailCredentialIds: ids })}
          error={visibleErrors.emailCredentialIds}
        />

        {showTemplates && (
          <Box className="flex flex-wrap items-center gap-1.5">
            <Box className="flex items-center gap-1 opacity-60">
              <AutoAwesomeIcon sx={{ fontSize: '14px' }} />
              <Typography fontSize={11}>
                {t('Start from a template')}
              </Typography>
            </Box>
            {templates.map((template) => (
              <Button
                key={template.label}
                size="small"
                onClick={() => applyTemplate(template)}
                sx={{
                  fontSize: '11px',
                  borderRadius: '9999px',
                  bgcolor: colors.card,
                  ':hover': { bgcolor: colors.info + '30' },
                }}
              >
                {template.label}
              </Button>
            ))}
          </Box>
        )}

        <VariableTextField
          label={t('Subject')}
          value={form.subject}
          onChange={(value) => update({ subject: value })}
          placeholder={t('Out of office until %%endDate%%')}
          varValues={varValues}
          dynamicDates={hideDates}
          error={visibleErrors.subject}
        />

        <Box
          className="flex flex-col gap-1"
          sx={{
            // Same chip rendering as the subject field (VariableTextField),
            // but styled on the embed's inner content node: Quill keeps its
            // caret guard nodes inside the outer span, so a pill drawn on the
            // outer span would paint the adjacent caret inside the chip.
            '& .ooo-chip > span[contenteditable="false"]': {
              display: 'inline-block',
              bgcolor: colors.info + '30',
              border: `1px solid ${colors.info}60`,
              borderRadius: '5px',
              padding: '0px 6px',
              margin: '0px 1px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            },
            '& .ooo-chip-pending > span[contenteditable="false"]': {
              bgcolor: colors.warning + '30',
              border: `1px solid ${colors.warning}60`,
              color: colors.warning,
            },
          }}
        >
          <Box className="flex flex-wrap items-center gap-1.5">
            <Typography fontSize={12} className="opacity-70 flex-1">
              {t('Message')}
            </Typography>
            {/* Inserts an atomic chip; the stored value keeps the raw token
                and the backend fills it when the reply is sent — in vacation
                mode per vacation. */}
            {OUT_OF_OFFICE_VARS.map((variable) => (
              <Button
                key={variable.key}
                size="small"
                onClick={() =>
                  bodyEditorRef.current?.insertDelta(
                    new Delta().insert({ oooVar: variable.key }),
                  )
                }
                sx={{
                  fontSize: '11px',
                  borderRadius: '9999px',
                  bgcolor: colors.card,
                  ':hover': { bgcolor: colors.info + '30' },
                }}
              >
                {`+ ${t(variable.label)}`}
              </Button>
            ))}
          </Box>
          <ExoRichTextEditor
            ref={bodyEditorRef}
            startHtml={initialBody}
            onHtmlChange={(html) =>
              update({
                body: isHtmlEmpty(html) ? '' : replaceVarChipsWithTokens(html),
              })
            }
            hasError={Boolean(visibleErrors.body)}
            allowSignature
            autoInsertSignature={false}
            signature={signature}
            emailCredentials={selectedMailboxes[0]}
          />
        </Box>
        {/* preview is hidden for now */}
        {/* <OutOfOfficePreview
          subject={form.subject}
          body={form.body}
          varValues={varValues}
          dynamicDates={hideDates}
          sender={sender}
        /> */}
      </Box>

      {/* controls */}
      <Box className="flex flex-row items-center justify-between pt-2 gap-2">
        <Button onClick={onCancel} variant="outlined" color="error">
          {t('Cancel')}
        </Button>
        <Box className="flex items-center gap-2">
          {showErrors && !isValid && (
            <Typography fontSize={11} sx={{ color: colors.error }}>
              {t('Please complete the highlighted fields')}
            </Typography>
          )}
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={isSaving}
            startIcon={
              isSaving ? (
                <CircularProgress size="14px" color="inherit" />
              ) : undefined
            }
          >
            {/* The vacation reply always exists — it is only ever saved. */}
            {isEdit || hideDates ? t('Save') : t('Create')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
